import { autoinject, bindable, Container } from 'aurelia-framework';
import { ListFieldTyped } from 'models/ListSettings';
import { CommentForEntity, MooringArticleSearchResult, MooringBuoy, MooringComponent } from 'models/mooring';
import { MooringArticleService, ToastService } from 'services';
import { MooringComponentSearchHandler, MooringOfferService, MooringService } from 'services/mooring';
import { toChecked, toKg, toLiter, toM } from 'lib/ui/ValueDisplayFormatters';
import { MooringTableBase } from '../table-base';
import { TableEntryManager } from 'services/ui-helpers';
import { DialogService } from 'aurelia-dialog';
import { PubSub } from 'lib/event/PubSub';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { AsyncErrorHandler } from 'lib/ui';
import { TableManager } from '../TableManager';
import { SearchResult } from 'elements/search/search-input';
import { MooringSelectEntriesForPupulateArgs } from 'features/mooring/component-templates/mooring-select-entries-for-populate';
import { I18N } from 'aurelia-i18n';
import { ensureNumberOrDefault } from 'lib/helpers';
import { FilterManager } from 'lib/tables/FilterManager';
import { Filters } from 'elements/Filter';
import { MaterialFactor } from 'models/mooring/MaterialFactor';
import { MaterialFactorService } from 'services/material-factor-service';
import { OfferViewContext } from '../../OfferViewContext';
import { calculateFactorsMbl } from '../factors';
import { CalculateUtilizationFactorDto, MooringStep } from 'models/mooring/UtilizationFactor';
import { MooringComponentSearchResult } from 'services/mooring/mooring-component-search-handler';

@autoinject
export class BuoysTable extends MooringTableBase<MooringBuoy> {
  protected table = new TableManager('buoys-table', tableColumns);
  protected context = OfferViewContext.useContext(this.container);

  @bindable
  protected onSave: (args: { entries: MooringBuoy[]; canNavigateOnSave: boolean }) => void;

  constructor(
    private container: Container,
    protected t: I18N,
    protected toast: ToastService,
    protected fm: FilterManager,
    dialogService: DialogService,
    mooringArticleService: MooringArticleService,
    // Used in HTML for article search
    mooringComponentSearchHandler: MooringComponentSearchHandler,
    tableEntryManager: TableEntryManager,
    pubsub: PubSub,
    confirmation: ConfirmDialog,
    mooringOfferService: MooringOfferService,
    mooringService: MooringService,
    materialFactorService: MaterialFactorService,
    materialFactors: MaterialFactor
  ) {
    super(
      'buoy',
      dialogService,
      mooringComponentSearchHandler,
      tableEntryManager,
      mooringArticleService,
      pubsub,
      confirmation,
      mooringOfferService,
      mooringService,
      materialFactorService,
      materialFactors
    );

    pubsub.sub('tabs:change-guard', async () => {
      if (this.changeTracker.areEqual(this.entries)) {
        this.pubsub.publish('tabs:change-guard-ok', null);
        return;
      }

      const res = await this.confirmation.confirmSave();

      if (res == 'cancel') {
        return;
      } else if (res === 'discard-continue') {
        this.pubsub.publish('tabs:change-guard-ok', null);
      } else {
        this.onSave({ entries: this.entries, canNavigateOnSave: true });
      }
    });
  }

  async attached() {
    await super.attached();

    this.fm.init({
      context: 'mooring-buoys',
      storageKey: 'mooring-buoys',
      filters: {} satisfies Filters,
      defaultFilters: {},
    });
  }

  protected setOrderByColumn(column: string) {
    this.fm.setOrderByColumn(column);
  }

  protected override postPasteAdjustment(index: number, name: (typeof tableColumns)[number]['field']) {
    this.onInputChange(index, name);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected setComponentQuantity(_: MooringComponent, __: MooringBuoy) {}

  protected onArticleSelected(searchResult: MooringComponentSearchResult, component: MooringComponent) {
    const res = searchResult.return;
    if (!res) return;
    component.ArticleProductId = res.Id;
    component.MBL = res.MBL;
    component.ProductName = res.ProductName;
    component.CategoryName = res.Category;
    component.TypeName = res.Type;
    component.ProductTypeName = res.ProductType;
    component.PrimaryDimension = res.PrimaryDimension;
    component.SecondaryDimension = res.SecondaryDimension;
    component.CategoryType = res.CategoryType;
    component.TypeVariant = res.TypeVariant;

    this.onSave({
      entries: this.entries,
      canNavigateOnSave: false,
    });
  }

  protected getEntriesForPopulate(): MooringSelectEntriesForPupulateArgs {
    const entries = structuredClone(this.entries);
    return {
      groups: [
        {
          _identifier: 'buoys',
          title: this.t.tr('mooring.buoy_plural'),
          options: entries
            .sort((a, b) => a.AnalysisNo.localeCompare(b.AnalysisNo))
            .map((x) => ({ name: x.AnalysisNo || '', meta: toLiter(x.Size), id: x.Id, selected: false, articles: x.Components?.length || 0 })),
        },
      ],
    };
  }

  protected onAddComponent() {
    const highestIndex = Math.max(0, ...this.entries.map((x) => x.SortIndex));
    this.entries.push({
      AnalysisNo: ``,
      SortIndex: highestIndex + 1,
    } as MooringBuoy);
  }

  protected moveBuoy(buoy: MooringBuoy, direction: 'up' | 'down') {
    const index = this.entries.indexOf(buoy);
    const newEntriesOrder = [...this.entries];

    if (direction === 'up' && index > 0) {
      [newEntriesOrder[index - 1], newEntriesOrder[index]] = [newEntriesOrder[index], newEntriesOrder[index - 1]];

      newEntriesOrder[index].SortIndex = index;
      newEntriesOrder[index - 1].SortIndex = index - 1;
    } else if (direction === 'down' && index < newEntriesOrder.length - 1) {
      [newEntriesOrder[index + 1], newEntriesOrder[index]] = [newEntriesOrder[index], newEntriesOrder[index + 1]];

      newEntriesOrder[index].SortIndex = index;
      newEntriesOrder[index + 1].SortIndex = index + 1;
    }

    this.entries = newEntriesOrder;
  }

  @AsyncErrorHandler
  protected async onDelete(index: number) {
    const res = await this.confirmation.confirmDelete();
    if (!res) return;

    const entry = this.entries[index];
    if (!entry) return;

    if (entry.Id) {
      await this.mooringService.deleteBuoy(this.mooringId, entry.Id);
    }

    this.entries.splice(index, 1);
    this.toast.showDeleted();
  }

  // This method is not used in this table, but added for consistency with other tables.
  protected onInputChange(index: number, name: (typeof tableColumns)[number]['field']) {
    const line = this.entries[index];

    if (name === 'Size' || name === 'PullDownKg') {
      const size = ensureNumberOrDefault(line.Size, 0);
      const pullDownKg = ensureNumberOrDefault(line.PullDownKg, 0);
      if (size && pullDownKg) {
        line.ResidualDriftKg = size - pullDownKg;
      }
    }

    if (name == 'IsAccident' || name == 'DimForce') {
      calculateFactorsMbl(line.DimForce, line, this.materialFactors, line.IsAccident);
      this.calculateUtilizationFactor(line.Components, line);

      line.MblMainStrap = line.DimForce * 5;
      line.MblAdditionalSafety = line.DimForce * 2.5;
    }
  }

  protected async calculateUtilizationFactor(component: MooringComponent[], line: MooringBuoy) {
    const calculateUtilizationFactorDtos: CalculateUtilizationFactorDto[] = [];
    component.forEach((component) => {
      calculateUtilizationFactorDtos.push({
        Id: component.Id,
        HasKnot: component.HasKnot,
        MblRopes: line.MblRopes,
        MblSinkRopes: line.MblSinkRopes,
        MblStraps: line.MblStraps,
      });
    });

    const response = await this.mooringService.calculateUtilizationFactor({
      mooringId: this.mooringId,
      mooringStep: MooringStep.BUOY,
      dto: calculateUtilizationFactorDtos,
    });

    response?.data.forEach((element) => {
      component.find((x) => x.Id === element.Id).UtilizationFactor = element.UtilizationFactor;
    });
  }

  protected save() {
    this.onSave({
      entries: this.entries,
      canNavigateOnSave: false,
    });
  }

  protected addEntryComment(component: MooringBuoy) {
    this.openCommentDialog({
      mooringId: this.mooringId,
      isReadOnly: false,
      componentId: component.Id,
      componentType: CommentForEntity.Buoy,
    });
  }

  protected addComponentComment(component: MooringComponent) {
    this.openCommentDialog({
      mooringId: this.mooringId,
      isReadOnly: false,
      componentId: component.Id,
      componentType: CommentForEntity.BuoyComponent,
    });
  }

  protected async clearForm() {
    const approved = await this.confirmation.confirmYesCancel('mooring.clearForm', 'mooring.clearFormConfirmation');

    if (approved) {
      const restedTable = this.resetFields(this.entries, fieldsToReset);
      this.entries = restedTable;
    }
  }

  protected resetFields<T extends Record<string, any>>(buoys: T[], fieldsToReset: (keyof T)[]): T[] {
    return buoys.map((buoy) => {
      const updatedBuoy = { ...buoy };

      fieldsToReset.forEach((field) => {
        const value = updatedBuoy[field];

        if (
          field === 'Size' ||
          field === 'PullDownKg' ||
          field === 'ResidualDriftKg' ||
          field === 'PullDownMeter' ||
          field === 'ResidualPullDownMeter'
        ) {
          (updatedBuoy[field] as number) = 0;
        } else if (typeof value === 'boolean') {
          (updatedBuoy[field] as boolean) = false;
        } else {
          updatedBuoy[field] = null;
        }
      });

      return updatedBuoy;
    });
  }

  protected unbind() {
    this.pubsub.unsub();
    super.unbind();
  }

  protected detached() {
    this.pubsub.unsub();
  }
}

const fieldsToReset: (keyof MooringBuoy)[] = [
  'MblRopes',
  'MblSinkRopes',
  'MblStraps',
  'MblAdditionalSafety',
  'MblMainStrap',
  'Size',
  'PullDownKg',
  'ResidualDriftKg',
  'PullDownMeter',
  'ResidualPullDownMeter',
  'IsLightBuoy',
];

const tableColumns: ListFieldTyped<MooringBuoy>[] = [
  {
    field: 'AnalysisNo',
    title: 'mooring.analysisNo',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: (value: string) => value,
    inputType: 'text',
  },
  {
    field: 'DimForce',
    title: 'mooring.dimForce',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'IsAccident',
    title: 'mooring.isAccident',
    helpText: 'mooring.dimForceDescription',
    selected: true,
    visible: true,
    filterType: 'BOOL',
    transform: toChecked,
    inputType: 'bool',
  },
  {
    field: 'MblRopes',
    title: 'mooring.minMblRope',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblSinkRopes',
    title: 'mooring.minMblSinkRope',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblStraps',
    title: 'mooring.minMblStrap',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblAdditionalSafety',
    title: 'mooring.minMblAdditionalSafety',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblMainStrap',
    title: 'mooring.minMblMainStrap',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'Size',
    title: 'mooring.sizeLiter',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toLiter,
    inputType: 'float',
  },
  {
    field: 'PullDownKg',
    title: 'mooring.pullDownKg',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'ResidualDriftKg',
    title: 'mooring.residualDrift',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'PullDownMeter',
    title: 'mooring.pullDownM',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toM,
    inputType: 'float',
  },
  {
    field: 'ResidualPullDownMeter',
    title: 'mooring.residualPullDownM',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toM,
    inputType: 'float',
  },
  {
    field: 'IsLightBuoy',
    title: 'mooring.isLightBuoy',
    selected: true,
    visible: true,
    transform: (v) => (v ? 'O' : ''),
    filterType: 'TEXT',
    inputType: 'bool',
  },
  // {
  //   field: 'Row',
  //   title: 'general.row',
  //   selected: true,
  //   visible: true,
  //   filterType: 'TEXT',
  //   inputType: 'int',
  // },
  // {
  //   field: 'Column',
  //   title: 'general.column',
  //   selected: true,
  //   visible: true,
  //   filterType: 'TEXT',
  //   inputType: 'int',
  // },
  {
    field: 'actions',
    title: '',
    selected: true,
    visible: true,
    filterType: 'none',
  },
] as const;
